import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { listCalendarEvents, createMeeting } from "../lib/api";

/**
 * App-wide meeting reminder watcher.
 *
 * Mounted once in the authenticated shell. While ANY Lisn tab is open, it
 * polls the connected Google Calendar and — when a timed event is about to
 * start (within LEAD_MIN minutes) — fires a browser notification + an
 * in-app toast with a "Take notes" action that drops the user straight into
 * a fresh meeting.
 *
 * Limitation by design: browser notifications only fire while a tab is open
 * (any page). Notifying when Lisn is fully closed needs Web Push + a service
 * worker + a reliable backend cron — deferred (see NEXT_STEPS).
 */
const LEAD_MIN = 2;           // notify when an event is this many minutes out
const EVENT_POLL_MS = 5 * 60 * 1000;  // refetch calendar every 5 min
const CHECK_MS = 30 * 1000;   // re-evaluate due events every 30s

function notifiedKey() {
    const d = new Date();
    return `lisn.reminders.${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export default function MeetingReminders() {
    const { user } = useAuth();
    const nav = useNavigate();
    const eventsRef = useRef([]);
    const notifiedRef = useRef(new Set());

    // Hydrate today's already-notified set so a reload doesn't re-alert.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(notifiedKey());
            if (raw) notifiedRef.current = new Set(JSON.parse(raw));
        } catch { /* ignore */ }
    }, []);

    const persistNotified = () => {
        try { localStorage.setItem(notifiedKey(), JSON.stringify([...notifiedRef.current])); } catch { /* ignore */ }
    };

    const startMeeting = useCallback(async (ev) => {
        try {
            const m = await createMeeting({ title: ev.title, context: ev.location || null });
            nav(`/meetings/${m.id}`, { state: { mode: ev.meet_link ? "online" : "inperson" } });
        } catch {
            toast.error("Couldn't start that meeting");
        }
    }, [nav]);

    const fireReminder = useCallback((ev) => {
        // Browser notification (works while on other tabs).
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
                const n = new Notification(`${ev.title} is starting`, {
                    body: "Tap to take notes with Lisn.",
                    tag: `lisn-meeting-${ev.id}`,
                    icon: "/favicon.ico",
                });
                n.onclick = () => { window.focus(); startMeeting(ev); n.close(); };
            } catch { /* ignore */ }
        }
        // In-app toast with an action (works while the tab is focused).
        toast(`${ev.title} is starting`, {
            description: "Take notes with Lisn?",
            duration: 30000,
            action: { label: "Take notes", onClick: () => startMeeting(ev) },
        });
    }, [startMeeting]);

    const fetchEvents = useCallback(async () => {
        try {
            const data = await listCalendarEvents(1); // only need today for reminders
            if (data.connected) eventsRef.current = data.events || [];
            else eventsRef.current = [];
        } catch {
            eventsRef.current = [];
        }
    }, []);

    const checkDue = useCallback(() => {
        const now = Date.now();
        for (const ev of eventsRef.current) {
            if (ev.all_day || !ev.start) continue;
            if (notifiedRef.current.has(ev.id)) continue;
            const start = new Date(ev.start).getTime();
            const minsOut = (start - now) / 60000;
            // Fire when the event is within the lead window and hasn't passed
            // by more than a minute (so a freshly-loaded page still catches a
            // just-started meeting).
            if (minsOut <= LEAD_MIN && minsOut >= -1) {
                fireReminder(ev);
                notifiedRef.current.add(ev.id);
                persistNotified();
            }
        }
    }, [fireReminder]);

    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        const boot = async () => {
            await fetchEvents();
            if (!cancelled) checkDue();
        };
        boot();
        const poll = setInterval(fetchEvents, EVENT_POLL_MS);
        const check = setInterval(checkDue, CHECK_MS);
        return () => { cancelled = true; clearInterval(poll); clearInterval(check); };
    }, [user, fetchEvents, checkDue]);

    return null;
}
